// src/contexts/AgentContext.tsx

import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import { Agent } from "../components/chat/AgentSelector";
import {
  getAgentConfig,
  getAgentFeatures,
  AgentConfiguration,
  AgentFeatures,
} from "../config/agentConfig";
import { createLogger } from "../utils/logger";
import { setAnalyticsContext } from "../utils/analytics";

const log = createLogger("AgentContext");

/**
 * Context interface providing agent state and configuration access
 */
interface AgentContextType {
  // Current agent
  currentAgent: Agent;
  currentAgentConfig: AgentConfiguration;
  currentAgentFeatures: AgentFeatures;
  availableAgents: Agent[];

  // Agent management
  setCurrentAgent: (agent: Agent) => void;
  setCurrentAgentById: (agentId: string) => void;

  // Utility methods
  isCurrentAgent: (agentId: string) => boolean;
  hasFeature: (featureName: keyof AgentFeatures) => boolean;
}

// Create context with undefined default
const AgentContext = createContext<AgentContextType | undefined>(undefined);

/**
 * Props for AgentProvider
 */
interface AgentProviderProps {
  children: React.ReactNode;
  initialAgentId?: string;
  availableAgents: Agent[];
}

/**
 * Agent Provider component that manages current agent state
 * and provides configuration access throughout the application
 */
export const AgentProvider: React.FC<AgentProviderProps> = ({
  children,
  initialAgentId = "ccm-space",
  availableAgents,
}) => {
  // Initialize with the appropriate agent
  const [currentAgent, setCurrentAgentState] = useState<Agent>(() => {
    const agent = availableAgents.find((a) => a.id === initialAgentId) || availableAgents[0];
    log.info("Initializing agent context", {
      requestedAgentId: initialAgentId,
      selectedAgent: agent.id,
      agentName: agent.name,
    });
    return agent;
  });

  // Get current configuration and features
  const currentAgentConfig = getAgentConfig(currentAgent.id);
  const currentAgentFeatures = currentAgentConfig.features;

  /**
   * Set the current agent
   */
  const setCurrentAgent = useCallback(
    (agent: Agent) => {
      log.info("Changing current agent", {
        from: currentAgent.id,
        to: agent.id,
        agentName: agent.name,
      });
      setCurrentAgentState(agent);
    },
    [currentAgent.id]
  );

  /**
   * Set current agent by ID
   */
  const setCurrentAgentById = useCallback(
    (agentId: string) => {
      const agent = availableAgents.find((a) => a.id === agentId);
      if (agent) {
        setCurrentAgent(agent);
      } else {
        log.warn("Attempted to set unknown agent", {
          attemptedId: agentId,
          availableIds: availableAgents.map((a) => a.id),
        });
      }
    },
    [availableAgents, setCurrentAgent]
  );

  /**
   * Check if the given agent ID is the current agent
   */
  const isCurrentAgent = useCallback(
    (agentId: string): boolean => {
      return currentAgent.id === agentId;
    },
    [currentAgent.id]
  );

  /**
   * Check if current agent has a specific feature enabled
   */
  const hasFeature = useCallback(
    (featureName: keyof AgentFeatures): boolean => {
      const value = currentAgentFeatures[featureName];

      // Handle boolean features
      if (typeof value === "boolean") {
        return value;
      }

      // For non-boolean features, check if they're defined and truthy
      return !!value;
    },
    [currentAgentFeatures]
  );

  // Keep the ambient analytics context pointing at the active agent, so every
  // tracked event can be broken down by agent without threading it per site.
  useEffect(() => {
    setAnalyticsContext({ agent_id: currentAgent.id });
  }, [currentAgent.id]);

  // Log agent changes for debugging
  useEffect(() => {
    log.debug("Agent context updated", {
      agentId: currentAgent.id,
      agentName: currentAgent.name,
      features: {
        showMetricsPanel: currentAgentFeatures.showMetricsPanel,
        allowFileUpload: currentAgentFeatures.allowFileUpload,
        chatEndpoint: currentAgentFeatures.chatEndpoint,
      },
    });
  }, [currentAgent, currentAgentFeatures]);

  const contextValue: AgentContextType = {
    currentAgent,
    currentAgentConfig,
    currentAgentFeatures,
    availableAgents,
    setCurrentAgent,
    setCurrentAgentById,
    isCurrentAgent,
    hasFeature,
  };

  return <AgentContext.Provider value={contextValue}>{children}</AgentContext.Provider>;
};

/**
 * Custom hook to access agent context
 * @throws Error if used outside of AgentProvider
 */
export const useAgent = (): AgentContextType => {
  const context = useContext(AgentContext);

  if (context === undefined) {
    throw new Error(
      "useAgent must be used within an AgentProvider. " +
        "Make sure your component is wrapped with <AgentProvider>."
    );
  }

  return context;
};

/**
 * HOC for components that need agent context
 */
export function withAgent<P extends object>(
  Component: React.ComponentType<P & { agentContext: AgentContextType }>
): React.FC<P> {
  const WrappedComponent: React.FC<P> = (props) => {
    const agentContext = useAgent();
    return <Component {...props} agentContext={agentContext} />;
  };

  WrappedComponent.displayName = `withAgent(${Component.displayName || Component.name})`;
  return WrappedComponent;
}
